import { Box, CircularProgress, IconButton } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../Services/api.service";
import { UI, btnSx } from "../components/ActionDrawer";
import DashTable, {
  FilterChips,
  KV,
  PageHead,
  RowCard,
  SearchBox,
  Sub2,
  Tag,
  Toolbar,
} from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
/* Keyed by the computed stage (see computeStage below), not the coarse
   order.status — that field only ever holds "agreement" for this whole span
   (draft through a signed-but-unpaid agreement) and a separate emi.status
   field for EMI orders, so neither alone tells you who's actually waiting
   on what. */
const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const WAITING_ON_META = {
  quotation: { role: "You", action: "Send the quotation", color: "#8E7CC3" },
  awaiting_response: {
    role: "Customer",
    action: "Accept the quotation",
    color: "#E5A03D",
  },
  advance_payment: {
    role: "Customer",
    action: "Pay the advance",
    color: "#3B82C4",
  },
  agreement_pending: {
    role: "You",
    action: "Record the signed agreement",
    color: "#3B82C4",
  },
  emi_pending: {
    role: "Customer",
    action: "Complete the EMI application",
    color: "#C46B3B",
  },
  emi_rejected: {
    role: "You",
    action: "Propose revised payment terms",
    color: "#D32F2F",
  },
  emi_approved: {
    role: "You",
    action: "Record the signed agreement",
    color: "#2E7D32",
  },
};

const STAGE_META = {
  quotation: { label: "Quotation", color: "#8E7CC3", tone: "stage" },
  awaiting_response: {
    label: "Awaiting response",
    color: "#E5A03D",
    tone: "s",
  },
  advance_payment: { label: "Advance payment", color: "#3B82C4", tone: "bl" },
  agreement_pending: { label: "Agreement pending", color: "#3B82C4", tone: "bl" },
  emi_pending: { label: "EMI pending", color: "#C46B3B", tone: "s" },
  emi_rejected: { label: "EMI rejected", color: "#D32F2F", tone: "b" },
  emi_approved: { label: "EMI approved", color: "#2E7D32", tone: "g" },
};

/* The three EMI sub-stages get one combined filter chip instead of three. */
const EMI_STAGES = ["emi_pending", "emi_rejected", "emi_approved"];

const PAGE_SIZE = 15;

const computeStage = (order) => {
  if (!order.quotePdf?.sent) return "quotation";
  if (!order.quotePdf?.signed) return "awaiting_response";
  if (order.quote?.paymentType === "emi") {
    if (order.emi?.status === "approved") return "emi_approved";
    if (order.emi?.status === "rejected") return "emi_rejected";
    return "emi_pending"; // not_started | documents_pending | under_review
  }
  if (order.status === "agreement") {
    // order.paymentStatus comes from the separate OrderPayment collection
    // (attached server-side in listOrders) — order.status alone can't tell
    // "still owes the advance" apart from "paid, waiting on the agreement".
    return order.paymentStatus === "paid_fully"
      ? "agreement_pending"
      : "advance_payment";
  }
  return order.status; // onboarding | mockup | ready_to_dispatch | dispatched | installation | activated
};

const StageChip = ({ order }) => {
  const stage = computeStage(order);
  const meta = STAGE_META[stage] || { label: stage, tone: "i" };
  return <Tag tone={meta.tone || "i"}>{meta.label}</Tag>;
};

const OwnerTag = ({ order }) => (
  <Tag tone={order.ownerId?.role?.includes("dealer") ? "s" : "i"}>
    {order.ownerId?.role?.includes("dealer") ? "DEALER" : "INTERNAL"}
  </Tag>
);

const WaitingOnLine = ({ order }) => {
  const waiting = WAITING_ON_META[computeStage(order)] || {};
  const dot = (color) => (
    <Box
      component="span"
      sx={{
        width: 7,
        height: 7,
        borderRadius: "50%",
        flexShrink: 0,
        bgcolor: color,
      }}
    />
  );
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "7px",
        fontSize: 12,
        color: waiting.complete ? UI.green : UI.ink3,
      }}
    >
      {dot(waiting.complete ? UI.green : waiting.color || UI.ink4)}
      {waiting.complete
        ? "Complete"
        : waiting.role
          ? `${waiting.role}: ${waiting.action}`
          : order.status}
    </Box>
  );
};

const QuoteActions = ({
  order,
  loadingQuoteId,
  downloadingQuoteId,
  onPreview,
  onDownload,
}) => (
  <Box sx={{ display: "flex", flexShrink: 0 }}>
    <IconButton
      size="small"
      onClick={(e) => onPreview(e, order._id)}
      title="Preview quote"
      disabled={loadingQuoteId === order._id}
    >
      {loadingQuoteId === order._id ? (
        <CircularProgress size={16} />
      ) : (
        <PictureAsPdfIcon sx={{ fontSize: 18, color: UI.p }} />
      )}
    </IconButton>
    <IconButton
      size="small"
      onClick={(e) => onDownload(e, order._id)}
      title="Download quote"
      disabled={downloadingQuoteId === order._id}
    >
      {downloadingQuoteId === order._id ? (
        <CircularProgress size={16} />
      ) : (
        <DownloadIcon sx={{ fontSize: 18, color: UI.ink3 }} />
      )}
    </IconButton>
  </Box>
);

const Orders = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [fullVisibility, setFullVisibility] = useState(false);
  const [loadingQuoteId, setLoadingQuoteId] = useState(null);
  const [downloadingQuoteId, setDownloadingQuoteId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);

  const fetchOrders = async (targetPage = page, keyword = debouncedSearch) => {
    setIsLoading(true);
    try {
      const res = await apiService.fetchOrders(
        token,
        targetPage,
        PAGE_SIZE,
        keyword,
        stageFilter === "all" ? undefined : stageFilter,
      );
      setOrders(res.data || []);
      setSummary(res.summary || null);
      setPagination(res.pagination || null);
      setFullVisibility(Boolean(res.fullVisibility));
    } catch (error) {
      toast.error("Error while fetching orders");
    } finally {
      setIsLoading(false);
    }
  };

  // Typing resets to page 1 and debounces the actual search request so each
  // keystroke doesn't fire its own network call.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [stageFilter]);

  useEffect(() => {
    fetchOrders(page, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, stageFilter]);

  // Stage filtering now happens server-side (see api.service.js fetchOrders
  // and listOrders) — `orders` already reflects the current stage filter,
  // scoped correctly across the whole dataset, not just this page.
  const filteredOrders = orders;

  const handlePreviewQuote = async (event, orderId) => {
    event.stopPropagation();
    setLoadingQuoteId(orderId);
    try {
      const res = await apiService.downloadQuote(orderId, token, false);
      if (res?.data?.url) {
        window.open(res.data.url, "_blank");
      } else {
        toast.error("Quote not available for this order yet.");
      }
    } catch (error) {
      toast.error("Quote not available for this order yet.");
    } finally {
      setLoadingQuoteId(null);
    }
  };
  const handleDownloadQuote = async (event, orderId) => {
    event.stopPropagation();
    setDownloadingQuoteId(orderId);
    try {
      const res = await apiService.downloadQuote(orderId, token, true);
      const { url } = res?.data || {};
      if (!url) {
        toast.error("Quote not available for this order yet.");
        return;
      }
      window.open(url, "_blank");
    } catch (error) {
      toast.error("Failed to download quote.");
    } finally {
      setDownloadingQuoteId(null);
    }
  };

  const kpis = [
    { label: "Open orders", value: summary?.openOrders ?? 0 },
    { label: "Awaiting response", value: summary?.awaitingResponse ?? 0 },
    { label: "Advance payment", value: summary?.advancePayment ?? 0 },
    {
      label: "EMI open",
      value: summary?.emiOpen ?? 0,
      alarm: (summary?.emiOpen ?? 0) > 0,
    },
  ];

  const stageOptions = [
    { value: "all", label: "All" },
    ...Object.entries(STAGE_META)
      .filter(([key]) => !EMI_STAGES.includes(key))
      .map(([key, meta]) => ({
        value: key,
        label: meta.label,
      })),
    { value: "emi", label: "EMI" },
  ];

  const columns = [
    { label: "Date" },
    { label: "Clinic" },
    { label: "Doctor" },
    { label: "Owner" },
    { label: "Product" },
    { label: "Payment term" },
    { label: "Stage" },
    { label: "Waiting on" },
    { label: "Quote" },
  ];

  const paymentTermLabel = (order) =>
    order.quote?.paymentTerm === "standard"
      ? "Standard 50 / 50"
      : order.quote?.paymentTerm;

  const tableRows = filteredOrders.map((order) => ({
    id: order._id,
    cells: [
      shortDate(order.createdAt),
      <>
        <Box component="span" sx={{ fontWeight: 500 }}>
          {order.clinic?.name || "—"}
        </Box>
        <Sub2>{order.clinic?.state}</Sub2>
      </>,
      order.doctor?.name || "—",
      <>
        <Box component="span" sx={{ fontWeight: 500 }}>
          {order.ownerId?.name || "—"}
        </Box>
        <Box sx={{ marginTop: "4px" }}>
          <OwnerTag order={order} />
        </Box>
      </>,
      order.quote?.product,
      paymentTermLabel(order),
      <StageChip order={order} />,
      <WaitingOnLine order={order} />,
      <QuoteActions
        order={order}
        loadingQuoteId={loadingQuoteId}
        downloadingQuoteId={downloadingQuoteId}
        onPreview={handlePreviewQuote}
        onDownload={handleDownloadQuote}
      />,
    ],
  }));

  return (
    <Box
      sx={{
        padding: { xs: "18px 16px 50px", md: "24px 28px 60px" },
        maxWidth: 1240,
      }}
    >
      <PageHead
        title="Orders"
        lede="Every order you have access to, and who it is waiting on."
        kpis={kpis}
      />

      <Toolbar
        right={
          <SearchBox
            value={searchQuery}
            placeholder="Search orders"
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        }
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: "10px",
            flexWrap: "wrap",
            marginBottom: "10px",
          }}
        >
          <Box sx={{ fontSize: 15, fontWeight: 500, color: UI.ink }}>
            {fullVisibility ? "All orders" : "My orders"}
          </Box>
          <Box sx={{ fontSize: 12, color: UI.ink3 }}>
            {fullVisibility ? "Full visibility" : "Scoped to your deals"}
          </Box>
        </Box>
        <FilterChips
          options={stageOptions}
          value={stageFilter}
          onChange={setStageFilter}
        />
      </Toolbar>

      {/* MOBILE: stacked cards */}
      {filteredOrders.length > 0 && (
        <Box
          sx={{
            display: { xs: "flex", sm: "none" },
            flexDirection: "column",
            gap: "10px",
            marginBottom: "28px",
          }}
        >
          {filteredOrders.map((order) => (
            <RowCard
              key={order._id}
              onClick={() => navigate(`/dashboard/orders/${order._id}`)}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ fontSize: 13, fontWeight: 500, color: UI.ink }}>
                    {order.clinic?.name || "—"}
                  </Box>
                  <Sub2>
                    {[shortDate(order.createdAt), order.clinic?.state]
                      .filter(Boolean)
                      .join(" · ")}
                  </Sub2>
                </Box>
                <QuoteActions
                  order={order}
                  loadingQuoteId={loadingQuoteId}
                  downloadingQuoteId={downloadingQuoteId}
                  onPreview={handlePreviewQuote}
                  onDownload={handleDownloadQuote}
                />
              </Box>

              <KV label="Doctor" value={order.doctor?.name || "—"} />
              <KV
                label="Owner"
                value={
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {order.ownerId?.name || "—"}
                    </Box>
                    <OwnerTag order={order} />
                  </Box>
                }
              />
              <KV label="Product" value={order.quote?.product} />
              <KV label="Payment term" value={paymentTermLabel(order)} />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  marginTop: "10px",
                  paddingTop: "10px",
                  borderTop: "1px solid",
                  borderTopColor: UI.line,
                }}
              >
                <Box>
                  <StageChip order={order} />
                </Box>
                <WaitingOnLine order={order} />
              </Box>
            </RowCard>
          ))}
        </Box>
      )}

      {/* DESKTOP: table */}
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <DashTable
          columns={columns}
          rows={tableRows}
          onRowClick={(id) => navigate(`/dashboard/orders/${id}`)}
          emptyText={
            orders.length === 0
              ? "No orders yet."
              : "No orders match this filter."
          }
          foot={`${filteredOrders.length} shown · ${pagination?.total ?? orders.length} total`}
        />
      </Box>

      {/* Empty state on mobile, where the table above is hidden. */}
      {!isLoading && filteredOrders.length === 0 && (
        <Box
          sx={{
            display: { xs: "block", sm: "none" },
            padding: "34px 18px",
            textAlign: "center",
            fontSize: 13,
            color: UI.ink3,
            bgcolor: UI.surface,
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "8px",
          }}
        >
          {orders.length === 0
            ? "No orders yet."
            : "No orders match this filter."}
        </Box>
      )}

      {pagination && pagination.total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "10px",
            fontSize: 13,
            color: UI.ink3,
          }}
        >
          <span>
            {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={pagination.page <= 1}
                sx={btnSx("ghost", pagination.page <= 1)}
              >
                Previous
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                disabled={pagination.page >= pagination.totalPages}
                sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
              >
                Next
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default Orders;
